/**
 * Connection, vehicle profile, location, what has been learned about the car,
 * and the raw adapter terminal.
 */
import { useEffect, useState } from 'react';
import {
  FUEL_CONSTANTS, GEAR_REJECTION_LABELS, LOCK_VERDICT_LABELS, TRANSMISSION_LABELS,
  compareIdentities, describeIdentity, isMismatch,
  type CycleName, type DrivingStyle, type LearnedPart, type ObdSession, type Transmission,
  type VehicleIdentity, type VehicleProfile,
} from '@obd/core';
import { useInstallPrompt, useSession, type AutoConnectStatus } from '../store/session';
import { geolocationAvailable, requestLocationPermission, type LocationPermission } from '../store/location';
import { bluetoothAvailability, isWebBluetoothAvailable } from '../transport/webBluetooth';
import type { AppSettings, LearnedOwners } from '../store/appState';
import type { CarRecordsApi } from '../store/records';
import { correctionText, formatSeconds, ordinal } from '../store/carProfile';
import { useLiveText } from './render';
import { FuelCard } from './FuelCard';
import { ProfileCard, type ProfileActions } from './ProfileCard';

/**
 * What the gear estimator is seeing right now, and why it is or is not
 * learning. Without this a car it cannot cope with is indistinguishable from
 * one it simply has not seen enough of yet -- both just sit there saying
 * nothing has been learned.
 */
function GearDiagnosticsPanel({ session }: { session: ObdSession }): JSX.Element {
  const ref = useLiveText<HTMLPreElement>(() => {
    const d = session.derived.gearEstimator.diagnostics;
    const rate = d.periodMs > 0 ? (1000 / d.periodMs).toFixed(1) + ' Hz' : 'no readings';
    const ratio = d.ratio === undefined ? '--' : d.ratio.toFixed(1);
    const verdict = d.locked ? 'locked up' : LOCK_VERDICT_LABELS[d.lock.why];
    const why = d.rejected === undefined ? 'learning' : GEAR_REJECTION_LABELS[d.rejected];
    return [
      ratio + ' rpm per km/h  (' + Math.round(d.rpm) + ' rpm, ' + Math.round(d.speedKph) + ' km/h)',
      'readings ' + rate + ' \u00b7 ' + verdict,
      why + ' \u00b7 ' + d.clusters + ' ratio' + (d.clusters === 1 ? '' : 's') +
        ' \u00b7 ' + formatSeconds(d.dwellS) + ' banked',
    ].join('\n');
  }, 200);
  return <pre ref={ref} className="gear-diagnostics small muted" />;
}

export interface SetupViewProps {
  settings: AppSettings;
  onSettings: (patch: Partial<AppSettings>) => void;
  /** Call after changing learned calibration by hand, so it is saved at once. */
  onLearnedChange: () => void;
  profile: ProfileActions;
  provenance: Provenance;
  /** This car's totals, fill-ups and trip log. */
  records: CarRecordsApi;
}

/**
 * Setup holds a dozen cards, most of which are touched once and never again.
 * Grouping them keeps the one you actually came for a tap away rather than a
 * scroll away -- which matters when the phone is in a stand.
 */
export type SetupSection = 'car' | 'fuel' | 'connection' | 'display' | 'data';

const SECTIONS: { id: SetupSection; label: string; hint: string }[] = [
  { id: 'car', label: 'Car', hint: 'Engine, gearbox, what the app has learned, and profiles' },
  { id: 'fuel', label: 'Fuel', hint: 'Fill-ups, real consumption and the fuel correction' },
  { id: 'connection', label: 'Connection', hint: 'Adapter, automatic connection, location and the simulator' },
  { id: 'display', label: 'Display', hint: 'Theme, drive mode and installing on the phone' },
  { id: 'data', label: 'Data', hint: 'What is stored on this phone' },
];

/** Which car each learned part came from, and how to change that. */
export interface Provenance {
  owners: LearnedOwners;
  /** The real car connected now, if any. */
  liveCar?: VehicleIdentity;
  onSetOwner: (part: LearnedPart, owner: VehicleIdentity | undefined) => void;
}

/** What this screen needs live: the accelerator for calibrating it, rpm to vet its source. */
const SETUP_SUBSCRIPTIONS: [string, number][] = [['accelerator', 100], ['rpm', 250]];

export function SetupView({
  settings, onSettings, onLearnedChange, profile, provenance, records,
}: SetupViewProps): JSX.Element {
  const {
    session, status, connecting, error, trace, fuelInference, simulator,
    connectBluetooth, connectSimulator, disconnect, clearTrace, autoConnect,
  } = useSession();
  // Opens where the driver most likely came for: connecting when there is no
  // car on the line, the car itself once there is.
  const [section, setSection] = useState<SetupSection>(
    () => (status.state === 'connected' ? 'car' : 'connection'),
  );

  const [simStyle, setSimStyle] = useState<DrivingStyle>('normal');
  const [simCycle, setSimCycle] = useState<CycleName>('mixed');
  const [simFuel, setSimFuel] = useState<'petrol' | 'diesel'>('petrol');
  const [showTrace, setShowTrace] = useState(false);
  const install = useInstallPrompt();

  const connected = status.state === 'connected';
  const ble = bluetoothAvailability();

  useEffect(() => {
    if (connected) session.setSubscriptions(SETUP_SUBSCRIPTIONS);
  }, [session, connected]);

  const setVehicle = (patch: Partial<VehicleProfile>): void => {
    const next = { ...settings.vehicle, ...patch };
    onSettings({ vehicle: next });
    session.setVehicle(next);
  };

  return (
    <div className="setup">
      <nav className="setup-nav" aria-label="Setup sections">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            className="chip"
            data-active={section === s.id ? 'true' : undefined}
            aria-pressed={section === s.id}
            title={s.hint}
            onClick={() => setSection(s.id)}
          >
            {s.label}
          </button>
        ))}
      </nav>

      {section === 'car' && (
        <>
        {/* ---------------------------------------------------------- vehicle */}
        <div className="card">
          <h3>Vehicle</h3>
          {fuelInference && (
            <p className="muted small">
              Detected <strong>{fuelInference.type}</strong> at{' '}
              {(fuelInference.confidence * 100).toFixed(0)}% confidence
              {fuelInference.reasons.length > 0 && ' — ' + fuelInference.reasons.slice(0, 2).join('; ')}.
            </p>
          )}
          <div className="field-row">
            <label className="field">
              <span>Car name</span>
              <input
                type="text"
                maxLength={60}
                value={settings.vehicle.name}
                onChange={(e) => setVehicle({ name: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Fuel type</span>
              <select
                value={settings.vehicle.fuelType}
                onChange={(e) => setVehicle({ fuelType: e.target.value as VehicleProfile['fuelType'], fuelTypeLocked: true })}
              >
                {Object.keys(FUEL_CONSTANTS).map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </label>
            <label className="field field-inline" style={{ alignSelf: 'end', paddingBottom: 6 }}>
              <input
                type="checkbox"
                checked={settings.vehicle.fuelTypeLocked}
                onChange={(e) => setVehicle({ fuelTypeLocked: e.target.checked })}
              />
              <span className="muted">Lock (stop auto-detection overriding it)</span>
            </label>
          </div>

          <div className="field-row">
            <NumField label="Displacement (L)" value={settings.vehicle.displacementL} step={0.1}
              onChange={(v) => setVehicle({ displacementL: v })} />
            <NumField label="Cylinders" value={settings.vehicle.cylinders} step={1}
              onChange={(v) => setVehicle({ cylinders: v })} />
            <NumField label="Forward gears" value={settings.vehicle.gearCount} step={1}
              onChange={(v) => {
                if (Number.isInteger(v) && v >= 1 && v <= 12) setVehicle({ gearCount: v });
              }} />
            <label className="field">
              <span>Gearbox</span>
              <select
                value={settings.vehicle.transmission}
                onChange={(e) => setVehicle({ transmission: e.target.value as Transmission })}
              >
                {(Object.keys(TRANSMISSION_LABELS) as Transmission[]).map((t) => (
                  <option key={t} value={t}>{TRANSMISSION_LABELS[t]}</option>
                ))}
              </select>
            </label>
            <NumField label="Volumetric efficiency" value={settings.vehicle.volumetricEfficiency} step={0.01}
              onChange={(v) => setVehicle({ volumetricEfficiency: v })} />
          </div>
          <div className="field-row">
            <NumField label="Redline (rpm)" value={settings.vehicle.redlineRpm} step={100}
              onChange={(v) => setVehicle({ redlineRpm: v })} />
            <NumField label="Economy shift point (rpm)" value={settings.vehicle.ecoShiftRpm} step={100}
              onChange={(v) => setVehicle({ ecoShiftRpm: v })} />
            <NumField label="Tank (L)" value={settings.vehicle.tankCapacityL} step={1}
              onChange={(v) => setVehicle({ tankCapacityL: v })} />
          </div>
          <p className="muted small">
            Displacement, cylinders and volumetric efficiency only matter when the car has no MAF
            sensor and no ECU fuel-rate PID — then they drive the speed-density estimate. The signals
            tab shows which method is actually in use under “Fuel Flow Source”. The gear indicator
            never shows a higher gear than “Forward gears”.
          </p>
        </div>
        <LearnedCard
          session={session}
          connected={connected}
          gpsEnabled={settings.gpsEnabled}
          onChange={onLearnedChange}
          provenance={provenance}
        />
        <ProfileCard actions={profile} vehicle={settings.vehicle} />
        </>
      )}

      {section === 'fuel' && <FuelCard records={records} session={session} vehicle={settings.vehicle} />}

      {section === 'connection' && (
        <>
        {/* ---------------------------------------------------------- connect */}
        <div className="card">
          <h3>Connection</h3>

          {error && <div className="error-card">{error}</div>}

          {!connected && (
            <>
              <p className="muted small">
                Your KL-DW006 is a Bluetooth Low Energy ELM327 clone, so the browser can talk to it
                directly — no app, no pairing in system settings. Turn the ignition on first: most
                adapters will not answer with the engine off.
              </p>
              {!ble.available ? (
                <>
                  <div className="error-card">{ble.reason}</div>
                  {ble.remedy === 'ios' && <IosBluetoothHelp />}
                </>
              ) : (
                <div className="button-row">
                  <button className="btn" data-variant="primary" disabled={connecting}
                    onClick={() => void connectBluetooth(false).catch(() => undefined)}>
                    {connecting ? 'Connecting…' : 'Connect adapter'}
                  </button>
                  <button className="btn" disabled={connecting}
                    onClick={() => void connectBluetooth(true).catch(() => undefined)}>
                    Show all Bluetooth devices
                  </button>
                </div>
              )}
              <p className="muted small" style={{ marginTop: 10 }}>
                If your adapter does not appear in the first list, it is advertising under an
                unexpected name — use “Show all Bluetooth devices”.
              </p>
            </>
          )}

          {connected && (
            <>
              <div className="kv-grid">
                <KV label="Adapter" value={status.adapter?.identifier ?? '—'} />
                <KV label="Protocol" value={status.adapter?.protocol ?? '—'} />
                <KV label="Responding ECUs" value={String(status.ecuCount)} />
                <KV label="Supported PIDs" value={String(status.supportedPidCount)} />
                <KV
                  label="Multi-PID batching"
                  value={status.adapter?.supportsBatch ? 'yes' : 'no — one at a time'}
                />
                <KV
                  label="Multi-frame replies"
                  value={
                    status.adapter?.supportsMultiFrame
                      ? 'yes — up to 6 PIDs per request'
                      : 'no — batches capped to one CAN frame'
                  }
                />
                <KV label="VIN" value={status.vin ?? 'not reported'} />
                <KV label="Battery at port" value={status.adapter?.voltage ? status.adapter.voltage.toFixed(1) + ' V' : '—'} />
                <KV label="Requests/s" value={status.scheduler.requestsPerSec.toFixed(1)} />
                <KV label="Values/s" value={status.scheduler.valuesPerSec.toFixed(1)} />
                <KV label="Round trip" value={status.scheduler.latencyMs.toFixed(0) + ' ms'} />
              </div>
              <button className="btn" style={{ marginTop: 12 }} onClick={() => void disconnect()}>
                Disconnect
              </button>
            </>
          )}
        </div>
          <AutoConnectCard
            status={autoConnect}
            enabled={settings.autoConnect}
            onToggle={(on) => onSettings({ autoConnect: on })}
            autoReconnect={settings.autoReconnect}
            onToggleReconnect={(on) => onSettings({ autoReconnect: on })}
          />
        <LocationCard settings={settings} onSettings={onSettings} session={session} />
        {/* -------------------------------------------------------- simulator */}
        <div className="card">
          <h3>Simulator</h3>
          <p className="muted small">
            A full ELM327 emulator with a physics-based car behind it. It speaks the real protocol at
            realistic adapter latency, so everything — dashboards, scoring, diagnostics — works exactly
            as it will in the car. Useful for building dashboards at your desk.
          </p>
          <div className="field-row">
            <label className="field">
              <span>Engine</span>
              <select value={simFuel} onChange={(e) => setSimFuel(e.target.value as 'petrol' | 'diesel')}>
                <option value="petrol">Petrol</option>
                <option value="diesel">Turbo diesel</option>
              </select>
            </label>
            <label className="field">
              <span>Route</span>
              <select value={simCycle} onChange={(e) => {
                const v = e.target.value as CycleName;
                setSimCycle(v);
                simulator?.setCycle(v);
              }}>
                <option value="urban">Urban stop-start</option>
                <option value="mixed">Mixed</option>
                <option value="motorway">Motorway</option>
                <option value="idle">Stationary idle</option>
              </select>
            </label>
            <label className="field">
              <span>Driving style</span>
              <select value={simStyle} onChange={(e) => {
                const v = e.target.value as DrivingStyle;
                setSimStyle(v);
                simulator?.setStyle(v);
              }}>
                <option value="calm">Calm</option>
                <option value="normal">Normal</option>
                <option value="aggressive">Aggressive</option>
              </select>
            </label>
          </div>
          <div className="button-row">
            <button
              className="btn"
              data-variant={connected ? undefined : 'primary'}
              disabled={connecting}
              onClick={() => void connectSimulator({ style: simStyle, cycle: simCycle, fuelType: simFuel })}
            >
              {simulator ? 'Restart simulator' : 'Start simulator'}
            </button>
            {simulator && (
              <span className="muted small">
                Change route or style above and it takes effect immediately — watch the score react.
              </span>
            )}
          </div>
        </div>
        {/* ---------------------------------------------------------- terminal */}
        <div className="card">
          <h3>
            Adapter terminal
            <button className="btn" data-size="sm" style={{ float: 'right' }} onClick={() => setShowTrace((v) => !v)}>
              {showTrace ? 'Hide' : 'Show'}
            </button>
          </h3>
          {showTrace && (
            <>
              <div className="trace">
                {trace.length === 0 && <span className="muted">Nothing yet.</span>}
                {trace.slice(-200).map((e) => (
                  <div key={e.id} className={'trace-line trace-' + e.direction}>
                    <span className="trace-dir">{e.direction === 'tx' ? '»' : e.direction === 'rx' ? '«' : 'i'}</span>
                    <span>{e.text}</span>
                  </div>
                ))}
              </div>
              <button className="btn" data-size="sm" style={{ marginTop: 8 }} onClick={clearTrace}>Clear</button>
            </>
          )}
        </div>
        </>
      )}

      {section === 'display' && (
        <>
        {/* ---------------------------------------------------------- install */}
        {!install.installed && (
          <div className="card">
            <h3>Install on your phone</h3>
            <div className="install-card">
              <div style={{ flex: '1 1 320px' }}>
                <p className="muted small" style={{ marginTop: 0 }}>
                  Installing gives you a home-screen icon, no browser chrome, and instant offline
                  start. The dashboard runs identically either way — this just makes it feel like an
                  app in the stand.
                </p>
                {install.available ? (
                  <button className="btn" data-variant="primary" onClick={() => void install.install()}>
                    Install
                  </button>
                ) : install.manualOnly ? (
                  <p className="small">
                    On iOS: Share → Add to Home Screen. Note that iOS cannot use Web Bluetooth at all,
                    so the adapter will not connect there — an Android phone is required for live data.
                  </p>
                ) : (
                  <ol className="install-steps">
                    <li>Open this page in Chrome over HTTPS (or on localhost).</li>
                    <li>Menu (⋮) → “Install app” or “Add to Home screen”.</li>
                    <li>Launch it from the home-screen icon.</li>
                  </ol>
                )}
              </div>
            </div>
          </div>
        )}
        {/* ------------------------------------------------------------ app */}
        <div className="card">
          <h3>Display &amp; drive mode</h3>
          <div className="field-row">
            <label className="field">
              <span>Theme</span>
              <select value={settings.theme} onChange={(e) => onSettings({ theme: e.target.value as 'dark' | 'light' })}>
                <option value="dark">Dark (night)</option>
                <option value="light">Light (daylight)</option>
              </select>
            </label>
            <label className="field">
              <span>Orientation in drive mode</span>
              <select
                value={settings.orientationLock}
                onChange={(e) => onSettings({ orientationLock: e.target.value as AppSettings['orientationLock'] })}
              >
                <option value="auto">Follow the phone</option>
                <option value="landscape">Lock landscape</option>
                <option value="portrait">Lock portrait</option>
              </select>
            </label>
          </div>

          <label className="field">
            <span>Screen dimming — {(settings.dimLevel * 100).toFixed(0)}%</span>
            <div className="slider-row">
              <input
                type="range"
                min={0}
                max={0.75}
                step={0.05}
                value={settings.dimLevel}
                onChange={(e) => onSettings({ dimLevel: Number(e.target.value) })}
              />
              <span className="muted small">
                Dims below the phone's own minimum for night driving.
              </span>
            </div>
          </label>

          <div className="field-row">
            <label className="field-inline">
              <input
                type="checkbox"
                checked={settings.keepAwake}
                onChange={(e) => onSettings({ keepAwake: e.target.checked })}
              />
              <span className="muted">Keep the screen awake while connected</span>
            </label>
            <label className="field-inline">
              <input
                type="checkbox"
                checked={settings.fullscreenInDriveMode}
                onChange={(e) => onSettings({ fullscreenInDriveMode: e.target.checked })}
              />
              <span className="muted">Go fullscreen in drive mode</span>
            </label>
            <label className="field-inline">
              <input
                type="checkbox"
                checked={settings.autoReconnect}
                onChange={(e) => onSettings({ autoReconnect: e.target.checked })}
              />
              <span className="muted">Reconnect automatically if the link drops</span>
            </label>
          </div>
          <p className="muted small">
            Drive mode hides the navigation and fills the screen. Dashboards with “Fit screen” enabled
            scale to the viewport so nothing has to be scrolled to while you are moving.
          </p>
        </div>
        </>
      )}

      {section === 'data' && <DataCard records={records} session={session} />}
    </div>
  );
}

/**
 * Connecting without being asked to.
 *
 * The browser can hand back an adapter it already has permission for, which is
 * what lets the app connect the moment it is opened in the car. What it cannot
 * do -- on any browser -- is wake itself up in the background, and saying so
 * plainly is better than leaving the driver wondering why nothing happened.
 */
function AutoConnectCard({
  status, enabled, onToggle, autoReconnect, onToggleReconnect,
}: {
  status: AutoConnectStatus;
  enabled: boolean;
  onToggle: (on: boolean) => void;
  autoReconnect: boolean;
  onToggleReconnect: (on: boolean) => void;
}): JSX.Element {
  return (
    <div className="card">
      <h3>Connect by itself</h3>
      <label className="field field-inline">
        <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)} />
        <span>Connect to my adapter whenever the app is open and the car is in range</span>
      </label>
      <label className="field field-inline">
        <input type="checkbox" checked={autoReconnect} onChange={(e) => onToggleReconnect(e.target.checked)} />
        <span>Reconnect automatically if the link drops mid-drive</span>
      </label>

      <div className="kv-grid" style={{ marginTop: 8 }}>
        <KV
          label="Remembered adapter"
          value={status.deviceName ?? (status.supported ? 'none yet — connect once' : 'not available')}
        />
        <KV
          label="Right now"
          value={
            !enabled ? 'off'
              : status.searching ? 'looking for the adapter…'
                : status.attempts > 0 ? 'out of range, retrying (' + status.attempts + ')'
                  : 'waiting'
          }
        />
      </div>

      <p className="muted small">
        {status.supported
          ? 'Get in, open the app, and it connects and starts logging on its own — no tap. Trips, gear '
            + 'learning and the fuel figures all pick up from there.'
          : 'This browser will not hand an app its Bluetooth devices back, so the first connection after '
            + 'opening the app needs one tap. In Chrome you can turn that on at '
            + 'chrome://flags/#enable-web-bluetooth-new-permissions-backend.'}
      </p>
      <p className="muted small">
        A web app cannot be woken by the car in the background: it connects while it is open, which
        in a phone stand is exactly when you want it to.
      </p>
    </div>
  );
}

/** What this phone is holding on to, and how to get rid of it. */
function DataCard({ records, session }: { records: CarRecordsApi; session: ObdSession }): JSX.Element {
  const life = session.summary('life');
  const usedMb = records.storage.usageBytes === undefined
    ? undefined
    : records.storage.usageBytes / 1_000_000;

  return (
    <div className="card">
      <h3>Stored on this phone</h3>
      <p className="muted small">
        Totals, fill-ups and the drive log live in this browser, one set per car. Nothing leaves the
        phone unless you export a car profile.
      </p>
      <div className="kv-grid">
        <KV label="Recorded for" value={records.key === 'unknown' ? 'no car identified yet' : records.key.replace(/^vin:/, 'VIN ')} />
        <KV label="Lifetime distance" value={life.distanceKm.toFixed(0) + ' km'} />
        <KV label="Lifetime fuel" value={life.fuelL.toFixed(0) + ' L'} />
        <KV label="Drives logged" value={String(records.trips.length)} />
        <KV label="Fill-ups" value={String(records.fills.length)} />
        <KV
          label="Space used"
          value={usedMb === undefined ? '—' : usedMb.toFixed(1) + ' MB'
            + (records.storage.quotaBytes ? ' of ' + (records.storage.quotaBytes / 1_000_000_000).toFixed(1) + ' GB' : '')}
        />
      </div>

      <p className="small" style={{ marginTop: 10 }}>
        {!records.storage.durable
          ? 'This browser gave the app no database — in a private window, or with site data blocked. '
            + 'Figures are kept only until the app is closed.'
          : records.storage.persisted
            ? 'This data is marked as durable: the browser will not clear it to reclaim space.'
            : 'The browser may clear this data if the phone runs short of space.'}
      </p>
      {records.storage.durable && !records.storage.persisted && (
        <div className="button-row">
          <button className="btn" onClick={() => void records.askForPersistence()}>Keep data on this device</button>
        </div>
      )}

      <div className="button-row" style={{ marginTop: 10 }}>
        <button className="btn" data-size="sm" onClick={() => void records.clearTripLog()}>Clear drive log</button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ location

function LocationCard({
  settings, onSettings, session,
}: {
  settings: AppSettings;
  onSettings: (patch: Partial<AppSettings>) => void;
  session: ObdSession;
}): JSX.Element {
  const { gps } = useSession();
  const accuracyRef = useLiveText<HTMLSpanElement>(
    // A phone standing still may go quiet for a while; the fix still stands.
    () => liveValue(session, 'gps_accuracy', (v) => '±' + v.toFixed(0) + ' m', 'waiting for a fix', 15_000),
    1000,
  );
  const speedRef = useLiveText<HTMLSpanElement>(
    () => liveValue(session, 'gps_speed', (v) => v.toFixed(0) + ' km/h', '—'),
    500,
  );
  const distanceRef = useLiveText<HTMLSpanElement>(
    () => liveValue(session, 'trip_distance_gps', (v) => v.toFixed(2) + ' km', '—', Infinity),
    1000,
  );

  const turnOn = (): void => {
    // Asked for here, inside the tap, so the browser ties the prompt to it.
    void requestLocationPermission();
    onSettings({ gpsEnabled: true });
  };

  return (
    <div className="card">
      <h3>Location</h3>
      <p className="muted small">
        With location on, the phone’s GPS measures how far off the car’s own speed reading is —
        typically a few percent, from tyre size and wear — and corrects speed, distance and
        consumption for it. The car’s reading stays the source, so nothing drops out in tunnels.
        Location is used on this device only and never leaves it.
      </p>

      {!geolocationAvailable() ? (
        <div className="error-card">This browser does not offer location.</div>
      ) : !settings.gpsEnabled ? (
        <div className="button-row">
          <button className="btn" data-variant="primary" onClick={turnOn}>
            Use phone location
          </button>
          <span className="muted small">Your browser will ask for permission.</span>
        </div>
      ) : (
        <>
          {gps.problem && <div className="error-card">{gps.problem}</div>}
          <div className="kv-grid">
            <KV label="Permission" value={PERMISSION_LABELS[gps.permission]} />
            <div className="kv">
              <span className="muted">Accuracy</span>
              <span className="tnum" ref={accuracyRef}>waiting for a fix</span>
            </div>
            <div className="kv">
              <span className="muted">GPS speed</span>
              <span className="tnum" ref={speedRef}>—</span>
            </div>
            <div className="kv">
              <span className="muted">GPS distance this trip</span>
              <span className="tnum" ref={distanceRef}>—</span>
            </div>
          </div>
          <div className="button-row" style={{ marginTop: 12 }}>
            {gps.permission === 'denied' && (
              <button className="btn" data-variant="primary" onClick={turnOn}>Try again</button>
            )}
            <button className="btn" onClick={() => onSettings({ gpsEnabled: false })}>
              Turn off location
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const PERMISSION_LABELS: Record<LocationPermission, string> = {
  unsupported: 'not available',
  unknown: 'requested',
  prompt: 'waiting for your answer',
  granted: 'allowed',
  denied: 'blocked',
};

/** A live signal, formatted, or a placeholder when it is missing or stale. */
function liveValue(
  session: ObdSession,
  key: string,
  format: (v: number) => string,
  placeholder: string,
  maxAgeMs = 5000,
): string {
  const state = session.store.get(key);
  if (state?.value === undefined || Date.now() - state.stats.lastUpdate > maxAgeMs) return placeholder;
  return format(state.value);
}

// ------------------------------------------------------------------ learned

/**
 * What has been worked out about this car, with a way to correct or forget
 * each part. It is all learned while driving and kept between drives.
 */
function LearnedCard({
  session, connected, gpsEnabled, onChange, provenance,
}: {
  session: ObdSession;
  connected: boolean;
  gpsEnabled: boolean;
  onChange: () => void;
  provenance: Provenance;
}): JSX.Element {
  const [note, setNote] = useState<string | null>(null);
  const derived = session.derived;
  const accelerator = derived.accelerator.status();
  const gears = derived.gearEstimator.gears();
  const factor = derived.gps.factor;
  const evidence = derived.gps.calibrationSeconds;

  const rawRef = useLiveText<HTMLSpanElement>(() => {
    const raw = derived.accelerator.status().raw;
    return raw === undefined ? '—' : raw.toFixed(1) + '%';
  }, 150);
  const calibratedRef = useLiveText<HTMLSpanElement>(
    () => liveValue(session, 'accelerator', (v) => v.toFixed(0) + '%', '—', 2000),
    150,
  );

  const pin = (end: 'rest' | 'full'): void => {
    if (derived.accelerator.pin(end)) {
      onChange();
      setNote(end === 'rest' ? 'This reading is now 0%.' : 'This reading is now 100%.');
    } else {
      setNote('There is no live accelerator reading to take this from yet.');
    }
  };
  const forget = (part: LearnedPart, what: string): void => {
    derived.resetLearned(part);
    // What is learned from now on belongs to the car it is learned on.
    provenance.onSetOwner(part, provenance.liveCar);
    onChange();
    setNote('Forgot the ' + what + '. It will be learned again as you drive.');
  };
  const keep = (part: LearnedPart, what: string): void => {
    provenance.onSetOwner(part, provenance.liveCar);
    setNote('Kept the ' + what + ' for this car.');
  };
  const origin = (part: LearnedPart, what: string, has: boolean): JSX.Element | null => (
    <Origin
      has={has}
      owner={provenance.owners[part]}
      liveCar={provenance.liveCar}
      onKeep={() => keep(part, what)}
      onForget={() => forget(part, what)}
    />
  );

  const sourceName = accelerator.source ? session.store.get(accelerator.source)?.def.name ?? accelerator.source : undefined;

  return (
    <div className="card">
      <h3>Learned about your car</h3>
      {note && <p className="small">{note}</p>}

      <h4 className="subhead">Accelerator</h4>
      <p className="muted small">
        Pedal and throttle sensors do not read 0% at rest — 15–30% is normal — so the reading is
        rescaled to run from 0% released to 100% floored. Both ends are learned as you drive; to set
        them now, with the ignition on, release the pedal and tap the first button, then hold it to
        the floor and tap the second.
      </p>
      <div className="kv-grid">
        <KV label="Read from" value={sourceName ?? (connected ? 'no pedal or throttle reading yet' : 'not connected')} />
        <div className="kv">
          <span className="muted">Sensor now</span>
          <span className="tnum" ref={rawRef}>—</span>
        </div>
        <div className="kv">
          <span className="muted">Shown as</span>
          <span className="tnum" ref={calibratedRef}>—</span>
        </div>
        <KV
          label="0% at"
          value={accelerator.rest === undefined ? '—' : accelerator.rest.toFixed(1) + '%' + (accelerator.restPinned ? ' (set by you)' : ' (learned)')}
        />
        <KV
          label="100% at"
          value={accelerator.full === undefined ? '—' : accelerator.full.toFixed(1) + '%' + (accelerator.fullPinned ? ' (set by you)' : ' (learned or assumed)')}
        />
      </div>
      <div className="button-row" style={{ marginTop: 10 }}>
        <button className="btn" data-size="sm" disabled={!accelerator.source} onClick={() => pin('rest')}>
          Pedal released — this is 0%
        </button>
        <button className="btn" data-size="sm" disabled={!accelerator.source} onClick={() => pin('full')}>
          Pedal floored — this is 100%
        </button>
        <button className="btn" data-size="sm" onClick={() => forget('accelerator', 'accelerator calibration')}>
          Re-learn
        </button>
      </div>
      {origin('accelerator', 'accelerator calibration', derived.accelerator.export().length > 0)}

      <h4 className="subhead">Gears</h4>
      <p className="muted small">
        Worked out from engine speed against road speed, only while the drivetrain is locked up.
        Numbering comes from the gear you pull away in, so it appears once you have driven off from
        a standstill and changed up.{' '}
        {session.vehicle.transmission === 'manual' || session.vehicle.transmission === 'dct'
          ? 'On a rigid gearbox it can also be worked out from the spacing of the ratios alone.'
          : session.vehicle.transmission === 'cvt'
            ? 'A stepless gearbox has no gears to find, so nothing is reported.'
            : 'A torque converter lets the engine run ahead of the gearbox, which distorts the '
              + 'spacing between ratios, so numbering here needs a pull-away.'}
      </p>
      <GearDiagnosticsPanel session={session} />
      {gears.length === 0 ? (
        <p className="small">Nothing learned yet.</p>
      ) : (
        <div className="gear-table">
          {gears.map((g) => (
            <div key={g.gear} className="gear-row">
              <span className="gear-name">{derived.gearEstimator.numberingIsKnown ? ordinal(g.gear) : '?'}</span>
              <span className="tnum">{g.ratio.toFixed(1)} rpm per km/h</span>
              <span className="muted small">
                {formatSeconds(g.dwellS)}{g.launch ? ' · pull-away gear' : ''}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="button-row" style={{ marginTop: 10 }}>
        <button className="btn" data-size="sm" onClick={() => forget('gears', 'gear ratios')}>
          Re-learn gears
        </button>
      </div>
      {origin('gears', 'gear ratios', gears.length > 0)}

      <h4 className="subhead">Speed correction</h4>
      <p className="small">
        {factor !== undefined
          ? speedVerdict(factor, evidence)
          : gpsEnabled
            ? 'Not enough GPS data yet: ' + Math.round(evidence) + ' of 30 seconds of steady driving above 30 km/h.'
            : 'Turn on location above to learn this.'}
      </p>
      <div className="button-row">
        <button className="btn" data-size="sm" onClick={() => forget('speed', 'speed correction')}>
          Re-learn (after changing tyres)
        </button>
      </div>
      {origin('speed', 'speed correction', evidence > 0)}
    </div>
  );
}

/**
 * Which car a learned part came from. A mismatch with the connected car is a
 * warning with two ways out; anything else is a quiet note.
 */
function Origin({
  has, owner, liveCar, onKeep, onForget,
}: {
  has: boolean;
  owner: VehicleIdentity | undefined;
  liveCar: VehicleIdentity | undefined;
  onKeep: () => void;
  onForget: () => void;
}): JSX.Element | null {
  if (!has) return null;
  const match = liveCar ? compareIdentities(owner, liveCar) : undefined;
  if (isMismatch(match)) {
    return (
      <div className="warn-card origin" role="alert" style={{ marginTop: 10 }}>
        <strong>Learned on a different car</strong> ({describeIdentity(owner)}). {match?.reason} It may not fit the car
        connected now.
        <div className="button-row" style={{ marginTop: 8 }}>
          <button className="btn" data-size="sm" onClick={onKeep}>Keep for this car</button>
          <button className="btn" data-size="sm" onClick={onForget}>Re-learn for this car</button>
        </div>
      </div>
    );
  }
  const same = match?.verdict === 'same' || match?.verdict === 'probably-same';
  return (
    <p className="muted small origin" style={{ marginTop: 6 }}>
      Learned on: {owner ? describeIdentity(owner) : 'a car that was not recorded'}
      {same ? ' — the car connected now' : ''}
    </p>
  );
}

function speedVerdict(factor: number, seconds: number): string {
  const basis = ' Based on ' + formatSeconds(seconds) + ' of steady driving; speed and distance are corrected for it.';
  return 'Your car’s speed ' + correctionText(factor) + '.' + basis;
}

function KV({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="kv">
      <span className="muted">{label}</span>
      <span className="tnum">{value}</span>
    </div>
  );
}

function NumField({
  label, value, step, onChange,
}: {
  label: string;
  value: number;
  step: number;
  onChange: (v: number) => void;
}): JSX.Element {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (Number.isFinite(v)) onChange(v);
        }}
      />
    </label>
  );
}

export { isWebBluetoothAvailable };

/**
 * What to do about Bluetooth on an iPhone or iPad.
 *
 * Both routes work by injecting `navigator.bluetooth`, so nothing in this app
 * changes -- once one is in place the normal Connect button appears and behaves
 * exactly as it does on Android.
 */
function IosBluetoothHelp(): JSX.Element {
  return (
    <div className="ios-help">
      <p className="small">
        Two ways to get Bluetooth on iOS. Both are free, neither needs this app rebuilt.
      </p>

      <div className="ios-option">
        <div className="ios-option-head">
          <strong>Safari extension</strong>
          <span className="pill" data-tone="good">keeps the home-screen app</span>
        </div>
        <p className="muted small">
          Adds Bluetooth to the real Safari, so this stays installed to your home screen with its
          own icon and drive mode works normally. Install the app, then enable it under
          Settings → Apps → Safari → Extensions.
        </p>
        <a className="btn" href="https://apps.apple.com/app/ioswebble/id6761301368"
          target="_blank" rel="noreferrer">Get iOSWebBLE</a>
      </div>

      <div className="ios-option">
        <div className="ios-option-head">
          <strong>A browser with its own Bluetooth stack</strong>
          <span className="pill">runs as a browser tab</span>
        </div>
        <p className="muted small">
          Bluefy is a browser with Web Bluetooth built in. Simplest option, but because iOS only
          lets Safari create home-screen apps, the dashboard runs as a tab rather than installed.
        </p>
        <a className="btn" href="https://apps.apple.com/app/bluefy-web-ble-browser/id1492822055"
          target="_blank" rel="noreferrer">Get Bluefy</a>
      </div>

      <p className="muted small">
        Either way the page must be served over HTTPS. An Android phone needs none of this —
        Chrome supports Bluetooth directly.
      </p>
    </div>
  );
}
